import { cn } from '../../../lib/utils';
import React, { useMemo } from 'react';
import DataTable from '../../common/DataTable';
import LevyPreviewModal from './LevyPreviewModal';
import { formatDateAU, formatDateOnlyAU } from '../../../utils/formatters';
import StatusTooltip from '../../../components/common/StatusTooltip';
import { Info } from 'lucide-react';

export const LEVY_COLUMN_CONFIG = [
  { id: 'ID', label: 'Log ID', defaultVisible: true },
  { id: 'Plan No.', label: 'Plan No.', defaultVisible: true },
  { id: 'Lot No.', label: 'Lot No.', defaultVisible: true },
  { id: 'Recipient', label: 'Recipient', defaultVisible: true },
  { id: 'Email', label: 'Email', defaultVisible: true },
  { id: 'Address', label: 'Address', defaultVisible: true },
  { id: 'Period', label: 'Levy due', defaultVisible: false },
  { id: 'Status', label: 'Status', defaultVisible: true },
  { id: 'Date', label: 'Date Processed', defaultVisible: true },
  { id: 'Method', label: 'Method', defaultVisible: true },
  { id: 'Actions', label: 'Actions', defaultVisible: true },
];

const statusConfig = {
  pending_pdf: { color: 'bg-[#fff9db] text-[#9a7b00]', label: 'Pending PDF' },
  ready_to_send: { color: 'bg-[#e5e7eb] text-[#4b5563]', label: 'Ready to Send' },
  sending_email: { color: 'bg-[#dae3ff] text-[#3e62e2]', label: 'Sending Email' },
  sending_post: { color: 'bg-[#dae3ff] text-[#3e62e2]', label: 'Sending Post' },
  emailed: { color: 'bg-[#d9fbe6] text-[#0a6e29]', label: 'Emailed' },
  posted: { color: 'bg-[#d9fbe6] text-[#0a6e29]', label: 'Posted' },
  needs_review: { color: 'bg-[#ffe0cc] text-orange-700', label: 'Needs Review' },
  failed_transient: { color: 'bg-[#ffe3e3] text-[#b42318]', label: 'Failed (Transient)' },
  failed_permanent: { color: 'bg-[#ffe3e3] text-[#b42318]', label: 'Failed (Permanent)' },
  skipped: { color: 'bg-gray-100 text-gray-800', label: 'Skipped' },
};
const StatusBadge = ({ status, reason, lastError, isDark }) => {
  const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
  const hasErrorTooltip = Boolean(lastError && status === 'needs_review');

  const badgeElement = (
    <span
      className={cn(
        config.color,
        "px-[16px] py-[6px] rounded-[78px] text-[13px] font-medium inline-flex items-center justify-center gap-1.5 whitespace-nowrap capitalize min-w-[120px]",
      )}
    >
      <span>{config.label}</span>
      {hasErrorTooltip && <Info className="w-3.5 h-3.5 stroke-[2.2] shrink-0 opacity-85" />}
    </span>
  );

  return (
    <div className="flex flex-col items-start gap-1 w-full max-w-[360px]">
      {hasErrorTooltip ? (
        <StatusTooltip message={lastError} isDark={isDark}>
          {badgeElement}
        </StatusTooltip>
      ) : (
        badgeElement
      )}

      {reason && status === 'needs_review' && (
        <p
          className={cn(
            "text-[14px] leading-[1.35] m-0 whitespace-normal min-w-[256px] break-words text-left font-normal w-full",
            isDark ? "text-[#b0b0b0]" : "text-[#555555]"
          )}
        >
          <b className={isDark ? "text-white" : "text-[#072837]"}>Reason:</b> {reason}
        </p>
      )}
    </div>
  );
};

const LevyTable = ({
  data = [],
  title = '',
  loading = false,
  isDark = false,
}) => {
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleView = (row) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const mutedTextClass = `font-medium text-[16px] ${isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'}`;
  const boldTextClass = `font-medium text-[16px] ${isDark ? 'text-white' : 'text-[#072837]'}`;

  const allColumns = useMemo(
    () => [
      {
        id: 'ID',
        header: 'Log ID',
        cell: ({ row }) => (
          <span className={mutedTextClass}>{row.original.id}</span>
        ),
      },
      {
        id: 'Plan No.',
        header: 'Plan No.',
        cell: ({ row }) => (
          <span className={boldTextClass}>{row.original.plan_number}</span>
        ),
      },
      {
        id: 'Lot No.',
        header: 'Lot No.',
        cell: ({ row }) => (
          <span className={mutedTextClass}>{row.original.lot_number}</span>
        ),
      },
      {
        id: 'Recipient',
        header: 'Recipient',
        cell: ({ row }) => (
          <span className={boldTextClass}>{row.original.recipient_name || 'Unknown'}</span>
        ),
      },
      {
        id: 'Email',
        header: 'Email',
        cell: ({ row }) => (
          <span className={mutedTextClass}>{row.original.recipient_email || '-'}</span>
        ),
      },
      {
        id: 'Address',
        header: 'Address',
        cell: ({ row }) => {
          const { postal_street, postal_town, postal_state, postal_postcode } = row.original;
          const addressParts = [postal_street, postal_town, postal_state, postal_postcode].filter(Boolean);
          const address = addressParts.join(', ');

          return (
            <span className={mutedTextClass}>{address || '-'}</span>
          );
        },
      },
      {
        id: 'Status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge
            status={row.original.status}
            reason={row.original.reason}
            lastError={row.original.last_error}
            isDark={isDark}
          />
        ),
      },
      {
        id: 'Period',
        header: 'Levy due',
        cell: ({ row }) => (
          <div className={cn("flex flex-col gap-[4px]")}>
            {row.original.due_date && <span className={mutedTextClass}>{formatDateOnlyAU(row.original.due_date)}</span>}
          </div>
        ),
      },
      {
        id: 'Date',
        header: 'Date Processed',
        cell: ({ row }) => (
          <span className={mutedTextClass}>{formatDateAU(row.original.updated_at)}</span>
        ),
      },
      {
        id: 'Method',
        header: 'Method',
        cell: ({ row }) => (
          <span className={mutedTextClass}>
            {row.original.recipient_kind === 'E' ? 'Email' : row.original.recipient_kind === 'P' ? 'Post' : '-'}
          </span>
        ),
      },
      {
        id: 'Actions',
        header: 'Actions',
        cell: ({ row }) => {
          const hasDocumentOrEmailId = Boolean(
            row.original.smata_document_id || row.original.front_message_uid
          );

          if (!hasDocumentOrEmailId) {
            return <span className={mutedTextClass}>-</span>;
          }

          return (
            <div className="flex flex-row items-center gap-[6px]">
              <button
                type="button"
                onClick={() => handleView(row.original)}
                className={`rounded-[10px] px-[8px] sm:px-[10px] py-[6px] flex items-center gap-[6px] border-0 cursor-pointer transition-colors whitespace-nowrap ${
                  isDark
                    ? 'bg-[#f5f5f5] text-[#000000] border border-[#292929] hover:bg-[#e8e8e8]'
                    : 'bg-[#072837] text-white hover:bg-[#0a3a4d]'
                }`}
                title="View Document"
              >
                <div
                  className="w-[14px] h-[14px] sm:w-[16px] sm:h-[16px]"
                  style={{
                    backgroundColor: isDark ? '#000000' : '#ffffff',
                    WebkitMaskImage: "url('/assets/icons/viewdata.svg')",
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskSize: 'contain',
                    WebkitMaskPosition: 'center',
                    maskImage: "url('/assets/icons/viewdata.svg')",
                    maskRepeat: 'no-repeat',
                    maskSize: 'contain',
                    maskPosition: 'center',
                    transition: 'background-color 0.2s ease'
                  }}
                  aria-hidden="true"
                />
                <span className="font-medium text-[14px] sm:text-[16px] transition-colors duration-200">View</span>
              </button>
            </div>
          );
        },
      },
    ],
    [mutedTextClass, boldTextClass]
  );

  return (
    <>
      <DataTable
        data={data}
        columns={allColumns}
        columnConfig={LEVY_COLUMN_CONFIG}
        storageKey="levy_visible_columns"
        title={title}
        loading={loading}
        isDark={isDark}
        emptyStateText="No levy automations found matching your criteria."
      />
      <LevyPreviewModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRow(null);
        }}
        data={selectedRow}
        isDark={isDark}
      />
    </>
  );
};

export default LevyTable;
